import type { CountdownApiClient } from "@countdown/api-client";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";

import { ConfirmButton } from "@/components/ConfirmButton";
import { HelpTip } from "@/components/HelpTip";
import { OutcomeNotice } from "@/components/Notice";
import { HistoryEntries, MailDialog } from "@/components/SponsorHistory";
import { Tick } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type ActionOutcome,
  LOAD_FAILED,
  type PrizeAllocation,
  type PrizeDoor,
  type PrizeDoorDetail,
  type ShippingAddress,
  loadPrizeDoor,
  loadPrizeDoorMail,
  markPrizeCompleted,
  markPrizeShipped,
  readShippingAddress,
  redrawPrize,
  useResource,
} from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/labels";
import { SPONSOR_WORDING, type Wording } from "@/lib/wording";

/** What the winner agreed to pass on (D96), in words. */
const FIELD_LABELS: Readonly<Record<string, string>> = {
  name: "Name",
  address: "Anschrift",
  email: "E-Mail-Adresse",
};

function fieldsText(fields: readonly string[]): string {
  const words = fields.map((field) => FIELD_LABELS[field] ?? field);
  return words.length < 2
    ? words.join("")
    : `${words.slice(0, -1).join(", ")} und ${words[words.length - 1] ?? ""}`;
}

type NextStep = PrizeDoor["next_step"];

/**
 * What waits, in words, and who acts (D95). `open` decides »Nur offene«: a door with nothing
 * left to do, or without a winner at all, leaves that view.
 */
export const NEXT_STEPS: Readonly<
  Record<NextStep, { readonly text: string; readonly actor: string | null; readonly open: boolean }>
> = {
  ship: { text: "Gewinn versenden", actor: "Sie", open: true },
  complete: { text: "Zustellung abschließen", actor: "Sie", open: true },
  redraw: { text: "Verfallen, keine Nachziehung mehr", actor: "Sie", open: true },
  // D96: at a firm's door the firm hands the prize over; the words name it (stepText).
  sponsor_ships: { text: "übermittelt den Gewinn", actor: "Sponsor", open: true },
  notify: { text: "Gewinnmail wird verschickt", actor: "System", open: true },
  await_winner: { text: "Wartet auf die Antwort des Gewinners", actor: "Gewinner", open: true },
  await_draw: { text: "Ziehung steht aus", actor: "System", open: true },
  no_participants: { text: "Keine Teilnahme, kein Gewinner", actor: null, open: false },
  done: { text: "Erledigt", actor: null, open: false },
};

/** The step as one line, with the reply deadline where a winner is awaited. */
export function stepText(
  step: NextStep,
  due: string | null | undefined,
  wording: Wording = SPONSOR_WORDING,
): string {
  const { text } = NEXT_STEPS[step];
  const line = step === "sponsor_ships" ? `${wording.nominative} ${text}` : text;
  return due === null || due === undefined ? line : `${line} bis ${formatDateTime(due)}`;
}

/** Who takes the step: »Kunde« instead of »Sponsor« in a customer's calendar (E8). */
export function actorOf(step: NextStep, wording: Wording = SPONSOR_WORDING): string | null {
  const { actor } = NEXT_STEPS[step];
  return actor === "Sponsor" ? wording.nominative : actor;
}

interface Step {
  readonly label: string;
  readonly at: string | null | undefined;
}

/**
 * The way of one allocation: drawn, notified, address, shipped, completed. A forfeited one ends
 * where it forfeited; the substitute after it has a way of its own.
 */
export function allocationSteps(allocation: PrizeAllocation, digital = false): readonly Step[] {
  const steps: Step[] = [
    { label: "Gezogen", at: allocation.drawn_at },
    { label: "Benachrichtigt", at: allocation.notified_at },
    { label: digital ? "Angenommen" : "Anschrift", at: allocation.confirmed_at },
    { label: digital ? "Übermittelt" : "Versendet", at: allocation.shipped_at },
    { label: "Abgeschlossen", at: allocation.completed_at },
  ];
  if (allocation.status !== "forfeited") {
    return steps;
  }
  const reached = steps.filter((step) => step.at !== null && step.at !== undefined);
  return [...reached, { label: "Verfallen", at: allocation.forfeited_at }];
}

/** »Gewinner«, then »Nachrücker 1«, »Nachrücker 2« in the order they were drawn. */
function allocationTitles(allocations: readonly PrizeAllocation[]): ReadonlyMap<string, string> {
  const titles = new Map<string, string>();
  let substitute = 0;
  let winner = 0;
  const several = allocations.filter((entry) => entry.replaces_prize_award_id === null).length > 1;
  for (const allocation of allocations) {
    if (allocation.replaces_prize_award_id === null) {
      winner += 1;
      titles.set(allocation.prize_award_id, several ? `Gewinner ${String(winner)}` : "Gewinner");
    } else {
      substitute += 1;
      titles.set(allocation.prize_award_id, `Nachrücker ${String(substitute)}`);
    }
  }
  return titles;
}

/** The addresses worth showing: an allocation that has one and still counts. */
function showsAddress(allocation: PrizeAllocation): boolean {
  return allocation.has_address && allocation.status !== "forfeited";
}

/**
 * Everything about the draw of one door (D95): the next step with its button, the prize and
 * the draw record, every winner with the way so far and the address, and the history of
 * activities and mails.
 *
 * THE ADDRESS IS READ WHEN THE DIALOG OPENS, once per opening. Every read is a POST that the
 * server logs (concept 10.6), so it stands in the history afterwards; a reload after a step
 * does not read it again.
 */
export function PrizeDoorDialog({
  actionId,
  position,
  onClose,
  onChanged,
  wording = SPONSOR_WORDING,
}: {
  readonly actionId: string;
  readonly position: number;
  /** »Sponsor« or »Kunde« (E8). */
  readonly wording?: Wording;
  readonly onClose: () => void;
  /** A step changed the door: the list behind the dialog reads it again. */
  readonly onChanged: () => void;
}): ReactElement {
  const load = useCallback(
    (api: CountdownApiClient) => loadPrizeDoor(api, actionId, position),
    [actionId, position],
  );
  const door = useResource<PrizeDoorDetail>(load);
  const [addresses, setAddresses] = useState<ReadonlyMap<string, ShippingAddress | null>>(
    new Map(),
  );
  const asked = useRef(new Set<string>());
  const [mailId, setMailId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);

  const data = door.status === "ready" ? door.data : null;
  useEffect(() => {
    for (const allocation of data?.allocations ?? []) {
      const id = allocation.prize_award_id;
      if (!showsAddress(allocation) || asked.current.has(id)) {
        continue;
      }
      asked.current.add(id);
      void readShippingAddress(id, actionId).then((address) => {
        setAddresses((known) => new Map(known).set(id, address));
      });
    }
  }, [data, actionId]);

  const refresh = (): void => {
    door.reload();
    onChanged();
  };

  const run = (action: () => Promise<ActionOutcome>): void => {
    setBusy(true);
    setOutcome(null);
    action()
      .then((result) => {
        setOutcome(result);
        if (result.ok) {
          refresh();
        }
      })
      .catch((error: unknown) => {
        console.error("[PrizeDoorDialog] step failed", error);
        setOutcome({ ok: false, message: "Der Schritt konnte nicht ausgeführt werden." });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Türchen {position}
            {door.status === "ready" ? ` · ${formatDate(door.data.calendar_date)}` : null}
          </DialogTitle>
          <DialogDescription>
            {door.status === "ready"
              ? (door.data.prize_description ?? "Kein Gewinn beschrieben")
              : door.status === "loading"
                ? "Wird geladen …"
                : LOAD_FAILED}
          </DialogDescription>
        </DialogHeader>

        {door.status === "ready" ? (
          <div className="grid gap-6">
            <NextStepLine door={door.data} wording={wording} />
            {outcome !== null ? <OutcomeNotice outcome={outcome} /> : null}

            <section aria-labelledby="gewinn-ziehung">
              <h3 id="gewinn-ziehung" className="mb-2 font-bold">
                Gewinn und Ziehung
              </h3>
              <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[10rem_1fr]">
                <dt className="text-muted-foreground">Gewinn</dt>
                <dd>{door.data.prize_description ?? "Nicht beschrieben"}</dd>
                <dt className="text-muted-foreground">Anzahl</dt>
                <dd>{door.data.prize_quantity ?? "Nicht festgelegt"}</dd>
                <dt className="text-muted-foreground">Übermittlung</dt>
                <dd>
                  {door.data.digital ? "Per E-Mail" : "Per Post"}
                  {door.data.own ? ", durch Sie" : `, durch den ${wording.oblique}`}
                </dd>
                {door.data.sponsor_name === null ? null : (
                  <>
                    <dt className="text-muted-foreground">Türchen von</dt>
                    <dd>{door.data.sponsor_name}</dd>
                  </>
                )}
                <dt className="text-muted-foreground">Ziehung</dt>
                <dd>
                  {door.data.draw === null
                    ? "Noch nicht gezogen"
                    : `${formatDateTime(door.data.draw.executed_at)}, ${String(door.data.draw.pool_size)} im Topf`}
                </dd>
              </dl>
            </section>

            {door.data.allocations.length === 0 ? null : (
              <Allocations
                door={door.data}
                wording={wording}
                allocations={door.data.allocations}
                addresses={addresses}
                busy={busy}
                run={run}
                refresh={refresh}
                actionId={actionId}
              />
            )}

            <section aria-labelledby="gewinn-verlauf">
              <h3 id="gewinn-verlauf" className="mb-2 font-bold">
                Verlauf
              </h3>
              <HistoryEntries entries={door.data.entries} onOpenMail={setMailId} />
            </section>
          </div>
        ) : null}

        {mailId === null ? null : (
          <MailDialog
            load={(api) => loadPrizeDoorMail(api, actionId, position, mailId)}
            onClose={() => {
              setMailId(null);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function NextStepLine({
  door,
  wording,
}: {
  readonly door: PrizeDoorDetail;
  readonly wording: Wording;
}): ReactElement {
  const step = NEXT_STEPS[door.next_step];
  const actor = actorOf(door.next_step, wording);
  return (
    <p
      className={
        step.open && step.actor === "Sie"
          ? "border-l-4 border-notice pl-3 text-sm"
          : "border-l-4 border-border pl-3 text-sm"
      }
    >
      <span className="font-bold">Nächster Schritt: </span>
      {stepText(door.next_step, door.due_at, wording)}
      {actor === null ? null : <span className="text-muted-foreground"> · {actor}</span>}
    </p>
  );
}

function Allocations({
  door,
  wording,
  allocations,
  addresses,
  busy,
  run,
  refresh,
  actionId,
}: {
  readonly door: PrizeDoorDetail;
  readonly wording: Wording;
  readonly allocations: readonly PrizeAllocation[];
  readonly addresses: ReadonlyMap<string, ShippingAddress | null>;
  readonly busy: boolean;
  readonly run: (action: () => Promise<ActionOutcome>) => void;
  readonly refresh: () => void;
  readonly actionId: string;
}): ReactElement {
  const titles = allocationTitles(allocations);
  return (
    <section aria-labelledby="gewinner" className="grid gap-5">
      <h3 id="gewinner" className="font-bold">
        Gewinner
      </h3>
      {allocations.map((allocation) => {
        const id = allocation.prize_award_id;
        const address = addresses.get(id);
        return (
          <div key={id} className="grid gap-2">
            <h4 className="text-sm font-bold">{titles.get(id)}</h4>
            <StepTrail allocation={allocation} digital={door.digital} />
            {allocation.handover_at === null || allocation.handover_at === undefined ? null : (
              <p className="text-sm">
                Weitergabe an {door.sponsor_name ?? `den ${wording.oblique}`} bestätigt am{" "}
                {formatDateTime(allocation.handover_at)}: {fieldsText(allocation.handover_fields ?? [])}
              </p>
            )}
            {allocation.response_deadline !== null && allocation.status === "notified" ? (
              <p className="text-sm text-muted-foreground">
                Rückmeldung bis {formatDateTime(allocation.response_deadline)}
              </p>
            ) : null}
            {allocation.shipping_note === null ? null : (
              <p className="text-sm">Vermerk zum Versand: {allocation.shipping_note}</p>
            )}
            {allocation.shipped_by === "sponsor" ? (
              <p className="text-sm text-muted-foreground">
                Gemeldet vom {wording.nominative} im {wording.portal}
              </p>
            ) : null}
            {!showsAddress(allocation) ? null : address === undefined ? (
              <p className="text-sm text-muted-foreground">Anschrift wird geladen …</p>
            ) : address === null ? (
              <p className="text-sm text-muted-foreground">Die Anschrift ließ sich nicht laden.</p>
            ) : (
              <address className="text-sm not-italic">
                {address.recipient_name}
                <br />
                {address.street}
                <br />
                {address.postal_code} {address.city}
                <br />
                {address.country_code}
                <span className="mt-1 block text-muted-foreground">
                  Kontakt: {address.email}
                  {address.delete_after === null ? null : (
                    <> · Zu löschen nach {formatDateTime(address.delete_after)}</>
                  )}
                </span>
              </address>
            )}
            <AllocationAction
              digital={door.digital}
              wording={wording}
              allocation={allocation}
              busy={busy}
              run={run}
              refresh={refresh}
              actionId={actionId}
            />
          </div>
        );
      })}
    </section>
  );
}

/** The way so far: done steps ticked with their date, the next one in bold. */
function StepTrail({
  allocation,
  digital,
}: {
  readonly allocation: PrizeAllocation;
  readonly digital: boolean;
}): ReactElement {
  const steps = allocationSteps(allocation, digital);
  const current = steps.findIndex((step) => step.at === null || step.at === undefined);
  return (
    <ol className="flex flex-wrap gap-x-5 gap-y-2 text-sm" aria-label="Ablauf">
      {steps.map((step, index) => {
        const done = step.at !== null && step.at !== undefined;
        return (
          <li
            key={step.label}
            className={
              done
                ? "flex items-start gap-1"
                : index === current
                  ? "flex items-start gap-1 font-bold"
                  : "flex items-start gap-1 text-muted-foreground"
            }
            aria-current={index === current ? "step" : undefined}
          >
            <Tick done={done} label={done ? "erledigt" : "offen"} />
            <span>
              {step.label}
              {done ? (
                <span className="block text-xs text-muted-foreground">
                  {formatDateTime(step.at ?? "")}
                </span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** The button of this allocation's step, where a person has one. */
function AllocationAction({
  digital,
  wording,
  allocation,
  busy,
  run,
  refresh,
  actionId,
}: {
  readonly digital: boolean;
  readonly wording: Wording;
  readonly allocation: PrizeAllocation;
  readonly busy: boolean;
  readonly run: (action: () => Promise<ActionOutcome>) => void;
  readonly refresh: () => void;
  readonly actionId: string;
}): ReactElement | null {
  const id = allocation.prize_award_id;
  if (allocation.next_step === "redraw") {
    // Asked first: the new winner gets the prize mail within the hour.
    return (
      <div>
        <ConfirmButton
          label="Neu ziehen"
          question="Für diesen Gewinn neu ziehen?"
          consequence="Aus dem Topf dieses Tages wird ein weiterer Gewinner gezogen, auch über die Zahl der Nachziehungen hinaus. Er erhält die Gewinnmail mit dem nächsten stündlichen Lauf."
          confirmLabel="Neu ziehen"
          disabled={busy}
          run={() => redrawPrize(id, actionId)}
          onDone={refresh}
        />
      </div>
    );
  }
  if (allocation.next_step === "ship") {
    return <DeliverForm id={id} digital={digital} busy={busy} run={run} actionId={actionId} />;
  }
  if (allocation.next_step === "sponsor_ships") {
    // The firm's step (D96); here only for the case the publisher has to step in.
    return (
      <details className="text-sm">
        <summary className="cursor-pointer">Für den {wording.oblique} übernehmen</summary>
        <div className="mt-2">
          <DeliverForm id={id} digital={digital} busy={busy} run={run} actionId={actionId} />
        </div>
      </details>
    );
  }
  if (allocation.next_step === "complete") {
    return (
      <div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => {
            run(() => markPrizeCompleted(id, actionId));
          }}
        >
          Abschließen
        </Button>
      </div>
    );
  }
  return null;
}

/**
 * Reporting the prize as handed over (D96), the same mask as in the portal: an optional
 * message the winner gets by mail in the firm's name, e.g. a voucher code, and for a parcel
 * the tracking number.
 */
function DeliverForm({
  id,
  digital,
  busy,
  run,
  actionId,
}: {
  readonly id: string;
  readonly digital: boolean;
  readonly busy: boolean;
  readonly run: (action: () => Promise<ActionOutcome>) => void;
  readonly actionId: string;
}): ReactElement {
  return (
    <form
      className="grid max-w-xl gap-2"
      aria-label="Gewinn als übermittelt melden"
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const note = String(data.get("vermerk") ?? "").trim();
        const message = String(data.get("nachricht") ?? "").trim();
        run(() =>
          markPrizeShipped(
            id,
            note === "" ? null : note,
            actionId,
            message === "" ? null : message,
          ),
        );
      }}
    >
      <label className="text-sm font-medium" htmlFor={`nachricht-${id}`}>
        Nachricht an den Gewinner
        <HelpTip topic="Nachricht an den Gewinner">
          Tragen Sie etwas ein, schicken wir es per E-Mail an den Gewinner, zum Beispiel den
          Gutscheincode. Leer bleibt das Feld, wenn der Gewinner schon direkt Bescheid bekam.
        </HelpTip>
      </label>
      <textarea
        className="min-h-20 border border-input bg-background px-3 py-1 text-sm"
        id={`nachricht-${id}`}
        name="nachricht"
        maxLength={2000}
        disabled={busy}
      />
      {digital ? null : (
        <>
          <label className="sr-only" htmlFor={`vermerk-${id}`}>
            Vermerk zur Sendung
          </label>
          <input
            className="w-56 border border-input bg-background px-3 py-1 text-sm"
            id={`vermerk-${id}`}
            name="vermerk"
            maxLength={200}
            placeholder="Sendungsnummer"
            disabled={busy}
          />
        </>
      )}
      <div>
        <Button type="submit" size="sm" disabled={busy}>
          {digital ? "Als übermittelt melden" : "Als versendet melden"}
        </Button>
      </div>
    </form>
  );
}
